import {
  ConfirmedResellerIcon,
  InfluencerIcon,
  IntermediateResellerIcon,
  MerchantIcon
} from "assets/icons";
import { t } from "i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Button, Checkbox, Input, RadioButton } from "ui";

import { ProductFormValues } from "../../ProductDetailsForm.types";
import { VariantBlockPartProps } from "./VariantBlock.types";

import styles from "../../ProductDetailsForm.module.scss";

export const VariantPricesBlock = ({ variantIndex }: VariantBlockPartProps) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext<ProductFormValues>();

  const bulkDiscountsWatch = watch(`variants.${variantIndex}.bulkDiscounts`);

  const activeCommissionTypeWatch = watch(
    `variants.${variantIndex}.activeCommissionType`
  );
  const commissionPercentageWatch = watch(
    `variants.${variantIndex}.commissionPercentage`
  );
  const commissionWatch = watch(`variants.${variantIndex}.commission`);

  const influencerCommissionPercentageWatch = watch(
    `variants.${variantIndex}.influencerCommissionPercentage`
  );

  const activeDiscountTypeWatch = watch(
    `variants.${variantIndex}.activeDiscountType`
  );
  const discountWatch = watch(`variants.${variantIndex}.discount`);
  const discountAmountWatch = watch(`variants.${variantIndex}.discountAmount`);

  const activeSuggestionDiscountTypeWatch = watch(
    `variants.${variantIndex}.activeSuggestionDiscountType`
  );
  const suggestionDiscountWatch = watch(
    `variants.${variantIndex}.suggestionDiscount`
  );
  const suggestionDiscountAmountWatch = watch(
    `variants.${variantIndex}.suggestionDiscountAmount`
  );

  // The field lives on the variant, but admins usually want one number for the
  // whole product — copy it across instead of retyping it per colour.
  const applySuggestionDiscountToAllVariants = () => {
    getValues("variants").forEach((_, index) => {
      if (index === variantIndex) {
        return;
      }

      setValue(
        `variants.${index}.activeSuggestionDiscountType`,
        activeSuggestionDiscountTypeWatch
      );
      setValue(
        `variants.${index}.suggestionDiscount`,
        suggestionDiscountWatch
      );
      setValue(
        `variants.${index}.suggestionDiscountAmount`,
        suggestionDiscountAmountWatch
      );
    });
  };

  return (
    <>
      <div className={styles.productForm__variantPrices}>
        <Controller
          control={control}
          name={`variants.${variantIndex}.buyingPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Buying Price")}
              label={t("Buying Price")}
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.merchantPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Merchant Price")}
              label={
                <>
                  <MerchantIcon />
                  {t("Merchant Price")}
                </>
              }
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.standardPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Standard Price")}
              label={t("Standard Price")}
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.intermediateResellerPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Intermediate Reseller Price")}
              label={
                <>
                  <IntermediateResellerIcon />
                  {t("Intermediate Reseller Price")}
                </>
              }
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.confirmedResellerPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Confirmed Reseller Price")}
              label={
                <>
                  <ConfirmedResellerIcon />
                  {t("Confirmed Reseller Price")}
                </>
              }
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.influencerPrice`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Influencer Price")}
              label={
                <>
                  <InfluencerIcon />
                  {t("Influencer Price")}
                </>
              }
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${variantIndex}.recommendedSellingPrice`}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value || ""}
              type="number"
              onChange={onChange}
              placeholder={t("Enter Recommended Selling Price")}
              label={t("Recommended Selling Price")}
              errorMessage={error?.message}
              maxLength={10}
            />
          )}
        />

        <div className={styles.productForm__variantPrices__commission}>
          {activeDiscountTypeWatch === "percentage" ? (
            <Input
              value={discountWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.discount`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Discount")}
              label={`${t("Discount")} (%)`}
              disabled={bulkDiscountsWatch.length !== 0}
              errorMessage={errors?.variants?.[variantIndex]?.discount?.message}
              maxLength={2}
            />
          ) : (
            <Input
              value={discountAmountWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.discountAmount`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Discount")}
              label={`${t("Discount")} (FCFA)`}
              disabled={bulkDiscountsWatch.length !== 0}
              errorMessage={
                errors?.variants?.[variantIndex]?.discountAmount?.message
              }
              maxLength={10}
            />
          )}

          <Controller
            control={control}
            name={`variants.${variantIndex}.activeDiscountType`}
            render={({ field: { value, onChange } }) => (
              <>
                <RadioButton
                  name={`variants.${variantIndex}.discountType`}
                  value="percentage"
                  label={t("Percentage")}
                  checked={value === "percentage"}
                  disabled={bulkDiscountsWatch.length !== 0}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("percentage");
                      setValue(`variants.${variantIndex}.discount`, null);
                      setValue(`variants.${variantIndex}.discountAmount`, null);
                    }
                  }}
                />
                <RadioButton
                  name={`variants.${variantIndex}.discountType`}
                  value="amount"
                  label={t("Fixed")}
                  checked={value === "amount"}
                  disabled={bulkDiscountsWatch.length !== 0}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("amount");
                      setValue(`variants.${variantIndex}.discount`, null);
                      setValue(`variants.${variantIndex}.discountAmount`, null);
                    }
                  }}
                />
              </>
            )}
          />
        </div>

        <div className={styles.productForm__variantPrices__commission}>
          {activeSuggestionDiscountTypeWatch === "percentage" ? (
            <Input
              value={suggestionDiscountWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.suggestionDiscount`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Discount If Suggested")}
              label={`${t("Discount If Suggested")} (%)`}
              errorMessage={
                errors?.variants?.[variantIndex]?.suggestionDiscount?.message
              }
              maxLength={2}
            />
          ) : (
            <Input
              value={suggestionDiscountAmountWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.suggestionDiscountAmount`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Discount If Suggested")}
              label={`${t("Discount If Suggested")} (FCFA)`}
              errorMessage={
                errors?.variants?.[variantIndex]?.suggestionDiscountAmount
                  ?.message
              }
              maxLength={10}
            />
          )}

          <Controller
            control={control}
            name={`variants.${variantIndex}.activeSuggestionDiscountType`}
            render={({ field: { value, onChange } }) => (
              <>
                <RadioButton
                  name={`variants.${variantIndex}.suggestionDiscountType`}
                  value="percentage"
                  label={t("Percentage")}
                  checked={value === "percentage"}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("percentage");
                      setValue(
                        `variants.${variantIndex}.suggestionDiscount`,
                        null
                      );
                      setValue(
                        `variants.${variantIndex}.suggestionDiscountAmount`,
                        null
                      );
                    }
                  }}
                />
                <RadioButton
                  name={`variants.${variantIndex}.suggestionDiscountType`}
                  value="amount"
                  label={t("Fixed")}
                  checked={value === "amount"}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("amount");
                      setValue(
                        `variants.${variantIndex}.suggestionDiscount`,
                        null
                      );
                      setValue(
                        `variants.${variantIndex}.suggestionDiscountAmount`,
                        null
                      );
                    }
                  }}
                />
              </>
            )}
          />

          <Button
            variant="text-delete"
            size="sm"
            type="button"
            onClick={applySuggestionDiscountToAllVariants}
          >
            {t("Apply to all variants")}
          </Button>
        </div>

        <div className={styles.productForm__variantPrices__commission}>
          {activeCommissionTypeWatch === "percentage" ? (
            <Input
              value={commissionPercentageWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.commissionPercentage`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Commission")}
              label={`${t("Commission")} (%)`}
              errorMessage={
                errors?.variants?.[variantIndex]?.commissionPercentage?.message
              }
              maxLength={2}
            />
          ) : (
            <Input
              value={commissionWatch ?? ""}
              type="number"
              onlyInteger={true}
              onChange={(e) =>
                setValue(
                  `variants.${variantIndex}.commission`,
                  +e.target.value || null
                )
              }
              placeholder={t("Enter Fixed Commission")}
              label={`${t("Commission")} (FCFA)`}
              errorMessage={
                errors?.variants?.[variantIndex]?.commission?.message
              }
            />
          )}

          <Controller
            control={control}
            name={`variants.${variantIndex}.activeCommissionType`}
            render={({ field: { value, onChange } }) => (
              <>
                <RadioButton
                  name={`variants.${variantIndex}.commissionType`}
                  value="percentage"
                  label="Percentage"
                  checked={value === "percentage"}
                  disabled={bulkDiscountsWatch.length !== 0}
                  onChange={(event) => {
                    if (event.currentTarget.checked) {
                      onChange("percentage");
                      setValue(`variants.${variantIndex}.commission`, null);
                      setValue(
                        `variants.${variantIndex}.commissionPercentage`,
                        null
                      );
                    }
                  }}
                />
                <RadioButton
                  name={`variants.${variantIndex}.commissionType`}
                  value="fixed"
                  label="Fixed"
                  checked={value === "fixed"}
                  onChange={(event) => {
                    if (event.currentTarget.checked) {
                      onChange("fixed");
                      setValue(`variants.${variantIndex}.commission`, null);
                      setValue(
                        `variants.${variantIndex}.commissionPercentage`,
                        null
                      );
                    }
                  }}
                />
              </>
            )}
          />
        </div>

        {/* Influencer commission is always a percentage of what the customer
            actually paid — the backend ignores any flat amount — so there is
            nothing to choose between here. */}
        <div className={styles.productForm__variantPrices__commission}>
          <Input
            value={influencerCommissionPercentageWatch ?? ""}
            type="number"
            onlyInteger={true}
            onChange={(e) =>
              setValue(
                `variants.${variantIndex}.influencerCommissionPercentage`,
                +e.target.value || null
              )
            }
            placeholder={t("Enter Influencer Commission")}
            label={`${t("Influencer Commission")} (%)`}
            errorMessage={
              errors?.variants?.[variantIndex]?.influencerCommissionPercentage
                ?.message
            }
            maxLength={2}
          />
        </div>
      </div>

      <Controller
        control={control}
        name={`variants.${variantIndex}.allowSellingBelowBuyingPrice`}
        render={({ field: { value, onChange } }) => (
          <Checkbox
            className={styles.productForm__bulkDiscountCheckbox}
            label={t("Allow Selling below buying price")}
            checked={value}
            onChange={onChange}
          />
        )}
      />
    </>
  );
};
